/**
 * TrackLabelsColumn Component
 *
 * Renders track labels in a column for the timeline.
 * This component is designed to scroll vertically in sync with the timeline tracks.
 *
 * Features:
 * - Renders TrackLabel for each track
 * - Track deletion confirmation flow using ConfirmDialog
 * - No header (Add button is now in the grid header)
 * - Height matches the track rows in the timeline
 *
 * Props:
 * - tracks: Array of tracks to display
 * - tools: Array of tools (used for deletion warning message)
 * - onDeleteTrack: Callback to delete a track by number
 * - onRenameTrack: Callback to rename a track
 */

import { useState, useMemo } from 'react';
import { TrackLabel } from './TrackLabel';
import { ConfirmDialog } from '../dialogs/ConfirmDialog';
import type { Track, Tool } from '../../stores/timelineStore';

interface TrackLabelsColumnProps {
  /**
   * Array of tracks to display
   */
  tracks: Track[];

  /**
   * Array of tools (used to count tools per track for deletion warning)
   */
  tools: Tool[];

  /**
   * Callback when a track should be deleted
   * @param trackNumber - The track number to delete
   */
  onDeleteTrack: (trackNumber: number) => void;

  /**
   * Callback when a track should be renamed
   * @param trackNumber - The track number to rename
   * @param newLabel - The new label for the track
   */
  onRenameTrack: (trackNumber: number, newLabel: string) => void;

  /**
   * Override for whether tracks can be deleted.
   * When undefined, defaults to tracks.length > 1.
   */
  canDelete?: boolean;
}

export function TrackLabelsColumn({
  tracks,
  tools,
  onDeleteTrack,
  onRenameTrack,
  canDelete,
}: TrackLabelsColumnProps) {
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [trackToDelete, setTrackToDelete] = useState<number | null>(null);

  const canDeleteTracks = canDelete ?? tracks.length > 1;

  // Get the count of tools on the track being deleted
  const toolCountForDeletion = useMemo(() => {
    if (trackToDelete === null) return 0;
    return tools.filter((t) => t.trackNumber === trackToDelete).length;
  }, [tools, trackToDelete]);

  /**
   * Handle delete button click on a track label
   * Opens the confirmation dialog
   */
  const handleDeleteClick = (trackNumber: number) => {
    setTrackToDelete(trackNumber);
    setDeleteDialogOpen(true);
  };

  /**
   * Handle confirmation of track deletion
   */
  const handleConfirmDelete = () => {
    if (trackToDelete !== null) {
      onDeleteTrack(trackToDelete);
      setTrackToDelete(null);
    }
  };

  /**
   * Handle cancellation of track deletion
   */
  const handleCancelDelete = () => {
    setTrackToDelete(null);
  };

  /**
   * Build the deletion warning message
   */
  const getDeleteDescription = (): string => {
    if (trackToDelete === null) return '';

    if (toolCountForDeletion === 0) {
      return `This will permanently delete Track ${trackToDelete}. This action cannot be undone.`;
    }

    return `This will permanently delete Track ${trackToDelete} and all ${toolCountForDeletion} tools on it. This action cannot be undone.`;
  };

  return (
    <>
      <div data-testid="track-labels-column">
        {tracks.map((track) => (
          <TrackLabel
            key={track.number}
            trackNumber={track.number}
            label={track.label}
            onDelete={() => handleDeleteClick(track.number)}
            onRename={(newLabel) => onRenameTrack(track.number, newLabel)}
            canDelete={canDeleteTracks}
          />
        ))}
      </div>

      {/* Delete Confirmation Dialog */}
      <ConfirmDialog
        open={deleteDialogOpen}
        onOpenChange={setDeleteDialogOpen}
        title="Delete Track"
        description={getDeleteDescription()}
        onConfirm={handleConfirmDelete}
        onCancel={handleCancelDelete}
        confirmText="Delete"
        cancelText="Cancel"
        destructive={true}
      />
    </>
  );
}
